import React, { useContext, useEffect, useState } from "react";
import { shallowEqual } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useActions } from "app/utils/hooks";
import { RootState } from "typedefs";
import { UserContext } from "app/contexts/UserContext";
import { WorkflowModel } from "app/models";
import { fetchWorkflows, createWorkflow } from "app/actions/workflow";
import SearchBar from "app/components/inputs/SearchBar";
import { Spinner } from "app/components/Loading";
import WorkflowItem from "./_components/WorkflowItem";
import CreateWorkflowModal from "./_components/CreateWorkflowModal";
import plus_circle from "app/images/plus_circle.svg";
import styles from "./styles.module.scss";

const WorkflowsList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const userContext = useContext(UserContext);
  const facilityId = userContext.facilityId;

  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data, pending, createPending, error } = useSelector(
    (state: RootState) => state.workflow,
    shallowEqual,
  );

  const workflowActions = useActions({
    fetchWorkflows,
    createWorkflow,
  });

  useEffect(() => {
    if (facilityId) {
      workflowActions.fetchWorkflows({ facilityId });
    }
  }, [facilityId]);

  const filteredWorkflows = data?.filter((w: WorkflowModel) =>
    w.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleCreate = (name: string) => {
    workflowActions
      .createWorkflow({ facilityId, name })
      .then((res: { id: string }) => {
        if (res?.id) {
          setIsModalOpen(false);
          history.push(`/automation/facility-workflows/workflow/${res.id}`);
        }
      });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header_wrapper}>
        <SearchBar
          className={styles.search}
          placeholder={t("Search...")}
          onChange={(val: string) => setSearchQuery(val)}
        />
        <button
          className={styles.new_workflow_btn}
          onClick={() => setIsModalOpen(true)}
        >
          <img src={plus_circle} alt="new workflow" />
          <p>{t("New Workflow")}</p>
        </button>
      </div>

      <div className={styles.workflow_list}>
        {pending && !data && <Spinner />}
        {!pending && error && !data && (
          <p className={styles.empty_text}>
            {t("Failed to load workflows. Please try again.")}
          </p>
        )}
        {!pending && !error && filteredWorkflows?.length === 0 && (
          <p className={styles.empty_text}>{t("No workflows found")}</p>
        )}
        {filteredWorkflows?.map((workflow: WorkflowModel) => (
          <WorkflowItem key={workflow.id} workflow={workflow} />
        ))}
      </div>

      <CreateWorkflowModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreate={handleCreate}
        pending={createPending}
      />
    </div>
  );
};

export default WorkflowsList;
